import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Heart, Home, ShoppingBag, CalendarDays, Users, Phone, MapPin, ArrowRight, CheckCircle2, Send, Smile, HandHelping, Clock, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SmartAnfrageForm from "@/components/SmartAnfrageForm";
import Header from "@/components/Header";
import BewerbenCTA from "@/components/BewerbenCTA";

const LEISTUNGEN = [
  { icon: Home, title: "Haushaltshilfe in Grefrath", desc: "Wir unterstützen Sie bei der Haushaltsführung, Wäsche, Aufräumen und der Sauberkeit in Ihrem Zuhause in Grefrath und den Ortsteilen Oedt und Vinkrath." },
  { icon: ShoppingBag, title: "Einkaufshilfe & Besorgungen", desc: "Einkäufe bei REWE, Aldi oder auf dem Grefrather Wochenmarkt — wir erledigen das für Sie oder begleiten Sie persönlich." },
  { icon: CalendarDays, title: "Begleitung zu Terminen", desc: "Arztbesuche in Grefrath oder Kempen, Behördengänge beim Rathaus Grefrath oder Therapien — wir bringen Sie sicher hin und zurück." },
  { icon: Smile, title: "Gesellschaft & Aktivitäten", desc: "Spaziergänge am Grefrather De Wittsee, gemeinsame Aktivitäten im Freilichtmuseum oder einfach nette Gespräche bei Ihnen zuhause." },
  { icon: Users, title: "Entlastung für Angehörige", desc: "Damit pflegende Angehörige in Grefrath auch mal durchatmen können — wir übernehmen stundenweise die Betreuung." },
  { icon: HandHelping, title: "Tagesstruktur & Routinen", desc: "Unterstützung bei der Tagesplanung, Medikamentenerinnerung und Begleitung durch den Alltag — verlässlich und persönlich." },
];

const FAQ_ITEMS = [
  { q: "Was kostet die Alltagsbegleitung in Grefrath?", a: "Für Pflegebedürftige mit Pflegegrad 1-5 ist die Alltagsbegleitung über den Entlastungsbetrag (131 € monatlich) finanzierbar — Sie zahlen keinen Eigenanteil. Wir rechnen direkt mit Ihrer Pflegekasse ab." },
  { q: "Wie schnell kann die Alltagsbegleitung in Grefrath starten?", a: "In der Regel können wir innerhalb von wenigen Tagen nach Ihrer Anfrage mit der Alltagsbegleitung in Grefrath beginnen. Rufen Sie uns an oder nutzen Sie das Formular auf dieser Seite." },
  { q: "Welche Ortsteile in Grefrath werden abgedeckt?", a: "Wir sind in ganz Grefrath aktiv, inklusive der Ortsteile Oedt und Vinkrath. Auch in den Nachbargemeinden rund um Grefrath – etwa Kempen, Viersen und am Niederrhein sind wir für Sie da." },
  { q: "Brauche ich einen Pflegegrad für die Alltagsbegleitung?", a: "Um die Kosten über den Entlastungsbetrag abrechnen zu können, benötigen Sie einen anerkannten Pflegegrad (mindestens Pflegegrad 1). Wir beraten Sie auch gerne zur Beantragung eines Pflegegrades." },
  { q: "Kann ich die Alltagsbegleitung auch stundenweise buchen?", a: "Ja, unsere Alltagsbegleitung ist flexibel buchbar — ob einmal pro Woche für zwei Stunden oder mehrmals wöchentlich. Wir passen uns Ihrem Bedarf an." },
  { q: "Wie unterscheidet sich Alltagsbegleitung von Pflegedienst?", a: "Ein Pflegedienst übernimmt medizinische und pflegerische Aufgaben. Die Alltagsbegleitung konzentriert sich auf alltagspraktische Hilfe: Haushalt, Einkaufen, Begleitung, Gesellschaft. Beides ergänzt sich perfekt." },
];

export default function AlltagsbegleitungGrefrath() {

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Alltagsbegleitung Grefrath | Haushaltshilfe & Betreuung | Lehnen & Weinacht";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Alltagsbegleitung in Grefrath: Haushaltshilfe, Einkaufshilfe, Begleitung zu Terminen und Entlastung für Angehörige. Ab Pflegegrad 1 über den Entlastungsbetrag finanzierbar. Jetzt anfragen!");

    // Schema.org LocalBusiness
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "schema-grefrath";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Lehnen & Weinacht – Alltagsbegleitung Grefrath",
      "description": "Professionelle Alltagsbegleitung in Grefrath: Haushaltshilfe, Einkaufsbegleitung, Betreuung und Entlastung für pflegende Angehörige.",
      "url": window.location.origin + "/alltagsbegleitung-grefrath",
      "telephone": "+491637917044",
      "email": "lehnenundweinacht@gmail.com",
      "address": { "@type": "PostalAddress", "streetAddress": "Hochstr. 72", "addressLocality": "Grefrath", "postalCode": "47929", "addressRegion": "NRW", "addressCountry": "DE" },
      "areaServed": [{ "@type": "City", "name": "Grefrath" }, { "@type": "City", "name": "Oedt" }, { "@type": "City", "name": "Vinkrath" }],
      "priceRange": "Über Entlastungsbetrag finanzierbar",
      "openingHours": "Mo-Fr 08:00-18:00",
      "sameAs": []
    });
    document.head.appendChild(schema);
    return () => { const el = document.getElementById("schema-grefrath"); if (el) el.remove(); };
  }, []);


  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="alltagsbegleitung-grefrath-page">
      <Header />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-green-50/60 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-cyan-700 transition-colors">Startseite</Link>
            <span>/</span>
            <Link to="/alltagsbegleitung" className="hover:text-cyan-700 transition-colors">Alltagsbegleitung</Link>
            <span>/</span>
            <span className="text-slate-600">Grefrath</span>
          </nav>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-100 rounded-full mb-6">
              <MapPin className="h-4 w-4 text-green-700" />
              <span className="text-sm font-semibold text-green-800">Alltagsbegleitung vor Ort in Grefrath</span>
            </div>
            <h1 className="font-['Manrope'] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-5">
              Alltagsbegleitung<br />
              <span className="text-green-700">in Grefrath</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-500 max-w-3xl leading-relaxed mb-8">
              Persönliche Unterstützung im Alltag für Pflegebedürftige und ihre Angehörigen in Grefrath, Oedt und Vinkrath. Haushaltshilfe, Einkaufsbegleitung und Betreuung — finanzierbar über den Entlastungsbetrag Ihrer Pflegekasse.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#anfrage-grefrath" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-8 py-4 text-lg font-semibold transition-all active:scale-[0.98] shadow-lg" data-testid="grefrath-cta-top">
                Jetzt in Grefrath anfragen
              </a>
              <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-200 hover:border-green-300 text-slate-700 rounded-xl px-8 py-4 text-lg font-semibold transition-all">
                <Phone className="h-5 w-5" /> 0163 791 7044
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Warum Alltagsbegleitung in Grefrath */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Warum Alltagsbegleitung in Grefrath?</h2>
          <div className="prose-custom space-y-4 text-base text-slate-600 leading-relaxed">
            <p>
              Grefrath ist eine lebenswerte Gemeinde am Niederrhein mit rund 15.000 Einwohnern. Viele ältere Menschen leben hier in ihren eigenen vier Wänden — in der Ortsmitte, in Oedt oder in Vinkrath. Doch mit zunehmendem Alter werden alltägliche Aufgaben oft zur Herausforderung: Der Weg zum Arzt wird beschwerlich, der Wocheneinkauf anstrengend, und pflegende Angehörige stoßen an ihre Grenzen.
            </p>
            <p>
              Genau hier setzt unsere Alltagsbegleitung an. Als <strong>anerkanntes Angebot zur Unterstützung im Alltag</strong> nach Landesrecht NRW helfen wir Ihnen direkt vor Ort in Grefrath — persönlich, verlässlich und vollständig finanzierbar über den Entlastungsbetrag Ihrer Pflegekasse.
            </p>
            <p>
              Unser Büro befindet sich in der <strong>Hochstraße 72, 47929 Grefrath</strong> — wir sind also immer in Ihrer Nähe.
            </p>
          </div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="grefrath-leistungen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Unsere Leistungen in Grefrath</h2>
            <p className="text-base text-slate-500 max-w-2xl mx-auto">Alltagsbegleitung, die sich Ihrem Leben anpasst — flexibel, menschlich und in Ihrer Nähe.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {LEISTUNGEN.map((l, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`grefrath-leistung-${i}`}>
                <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <l.icon className="h-6 w-6 text-green-700" />
                </div>
                <h3 className="font-['Manrope'] text-lg font-bold text-slate-900 mb-2">{l.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Finanzierung */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-50 rounded-2xl border border-green-200 p-8 sm:p-10">
            <h2 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-4">Kostenübernahme durch Ihre Pflegekasse</h2>
            <p className="text-base text-slate-600 leading-relaxed mb-6">
              Unsere Alltagsbegleitung in Grefrath ist als anerkanntes Angebot nach § 45a SGB XI zugelassen. Die Kosten werden vollständig über den <strong>Entlastungsbetrag</strong> (131 € monatlich) abgerechnet — für Sie fallen <strong>keine Eigenkosten</strong> an.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">131 €</p>
                <p className="text-xs text-slate-500 mt-1">monatlicher Entlastungsbetrag</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">Ab PG 1</p>
                <p className="text-xs text-slate-500 mt-1">bereits ab Pflegegrad 1</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-green-100 text-center">
                <p className="text-2xl font-extrabold text-green-700 font-['Manrope']">0 €</p>
                <p className="text-xs text-slate-500 mt-1">Eigenanteil für Sie</p>
              </div>
            </div>
            <p className="text-sm text-slate-500">Nicht genutzte Beträge können übertragen werden. Wir übernehmen die Abrechnung mit Ihrer Pflegekasse.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="grefrath-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-8 text-center">Häufige Fragen zur Alltagsbegleitung in Grefrath</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200/60 rounded-xl px-5 shadow-sm">
                <AccordionTrigger className="text-base font-semibold text-slate-800 hover:text-green-700 hover:no-underline py-4">{item.q}</AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed pb-4">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-['Manrope'] text-lg font-bold text-slate-900 mb-4">Alltagsbegleitung auch in Ihrer Nähe</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Link to="/alltagsbegleitung-rheinberg" className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:shadow-md transition-all text-center group">
              <MapPin className="h-5 w-5 text-green-600 mx-auto mb-1" />
              <p className="font-semibold text-slate-800 group-hover:text-green-700">Alltagsbegleitung Rheinberg (ab 01.01.2027)</p>
            </Link>
            <Link to="/alltagsbegleitung" className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:shadow-md transition-all text-center group">
              <Heart className="h-5 w-5 text-green-600 mx-auto mb-1" />
              <p className="font-semibold text-slate-800 group-hover:text-green-700">Alle Einsatzgebiete</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Anfrage-Formular */}
      <BewerbenCTA stadtName="Grefrath" stadtSlug="grefrath" />
      <section className="py-16 bg-[#F8FAFC]" id="anfrage-grefrath" data-testid="grefrath-anfrage">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <SmartAnfrageForm stadtName="Grefrath" stadtSlug="grefrath" testIdPrefix="grefrath" showHeadline />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung</p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <Link to="/alltagsbegleitung" className="hover:text-white transition-colors">Alltagsbegleitung</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Ratgeber</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
